import { useState } from 'react';
import type { UserDoc } from '../../shared/types';
import { DAILY_NAME, previousDay } from '../../shared/constants';
import { IconClose } from './ui';

/** Shorter than this is not a run worth a reminder; one day is just yesterday. */
const SHOWN_FROM_DAYS = 2;

/**
 * How long a run is on the line right now, or null when it is not.
 *
 * The same test as the late push in functions/src/daily.ts: the run carried
 * through the day before the streak day, and the prompt that is live has not
 * been answered. `streakDay` is the day the app shows, which trails the
 * calendar until today's prompt fires.
 */
export function runAtRisk(
  user: Pick<UserDoc, 'streak' | 'lastDailyDay'> | undefined,
  streakDay: string,
  promptOpen: boolean,
): number | null {
  const days = user?.streak ?? 0;
  if (!promptOpen || days < SHOWN_FROM_DAYS) return null;
  return user?.lastDailyDay === previousDay(streakDay) ? days : null;
}

const KEY = 'givisnap:streakNudgeClosed';

function closedFor(day: string): boolean {
  try {
    return localStorage.getItem(KEY) === day;
  } catch {
    return false;
  }
}

/**
 * A run on the line, said in the app as well as by the evening's push -- which
 * only reaches someone with notifications on, and only once, late. Under the
 * chat's header, where everybody already is; a tap opens the camera, and the
 * cross puts it away until the next streak day.
 */
export function StreakNudge({
  days,
  streakDay,
  onPost,
}: {
  days: number | null;
  streakDay: string;
  onPost: () => void;
}) {
  const [closed, setClosed] = useState(() => closedFor(streakDay));
  if (days === null || closed || closedFor(streakDay)) return null;

  const close = () => {
    setClosed(true);
    try {
      localStorage.setItem(KEY, streakDay);
    } catch {
      // Blocked storage: it comes back next time the app opens.
    }
  };

  return (
    <div className="streak-nudge" role="status">
      <button type="button" className="streak-nudge-open" onClick={onPost}>
        <span className="streak-nudge-days">🔥 {days}-day run</span>
        <span className="streak-nudge-line">Post today&rsquo;s {DAILY_NAME} to keep it going</span>
      </button>
      <button type="button" className="streak-nudge-close" aria-label="Not now" onClick={close}>
        <IconClose size={14} />
      </button>
    </div>
  );
}
